import { useState } from "react";
import PageHero from "@/components/PageHero";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CONTACT, BRANCHES } from "@/lib/data";
import { Phone, Mail, MapPin, Send, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";

export default function Contact() {
  const [form, setForm] = useState({ name:"", company:"", mobile:"", email:"", subject:"", message:"" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent successfully");
      setForm({ name:"", company:"", mobile:"", email:"", subject:"", message:"" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Get in Touch"
        title={<>Let's <em className="not-italic font-serif italic text-shramik-green">build something</em> together.</>}
        subtitle="Partnerships, distribution, careers, CSR collaborations, or media enquiries — we'd love to hear from you."
      />

      <section className="py-16">
        <div className="container-page grid lg:grid-cols-12 gap-10">
          {/* Contact info */}
          <div className="lg:col-span-4 space-y-6">
            <motion.a href={`tel:${CONTACT.phoneRaw}`} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} data-testid="contact-phone"
              className="flex items-start gap-4 p-6 rounded-2xl bg-white border border-slate-100 hover:border-shramik-green transition-all">
              <div className="w-12 h-12 rounded-xl bg-shramik-greenLight flex items-center justify-center text-shramik-green"><Phone className="w-5 h-5"/></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Call us</div>
                <div className="font-display text-lg font-medium mt-0.5">{CONTACT.phone}</div>
                <div className="text-sm text-slate-500 mt-1">Mon–Sat, 9:00 to 19:00</div>
              </div>
            </motion.a>
            <motion.a href={`mailto:${CONTACT.email}`} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.1}} data-testid="contact-email"
              className="flex items-start gap-4 p-6 rounded-2xl bg-white border border-slate-100 hover:border-shramik-green transition-all">
              <div className="w-12 h-12 rounded-xl bg-shramik-greenLight flex items-center justify-center text-shramik-green"><Mail className="w-5 h-5"/></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Email</div>
                <div className="font-display text-lg font-medium mt-0.5">{CONTACT.email}</div>
                <div className="text-sm text-slate-500 mt-1">We respond within 24 hrs</div>
              </div>
            </motion.a>
            <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.2}} data-testid="contact-address"
              className="flex items-start gap-4 p-6 rounded-2xl bg-white border border-slate-100">
              <div className="w-12 h-12 rounded-xl bg-shramik-greenLight flex items-center justify-center text-shramik-green"><MapPin className="w-5 h-5"/></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Head Office</div>
                <div className="font-display text-base font-medium mt-0.5">{CONTACT.address}</div>
              </div>
            </motion.div>
            <a href={`https://wa.me/${CONTACT.phoneRaw}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
              className="flex items-start gap-4 p-6 rounded-2xl bg-shramik-green text-white hover:bg-shramik-greenDark transition-colors">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center"><MessageCircle className="w-5 h-5"/></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/70">WhatsApp</div>
                <div className="font-display text-lg font-medium mt-0.5">Chat instantly</div>
              </div>
            </a>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-100 p-8 md:p-10" data-testid="contact-form">
              <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight leading-tight">Send us a message</h2>
              <p className="text-slate-500 mt-2 text-sm">Fields marked * are required.</p>

              <div className="mt-8 grid md:grid-cols-2 gap-5">
                <input required data-testid="cf-name" placeholder="Full name *" className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-shramik-green" value={form.name} onChange={e => setForm({...form, name:e.target.value})}/>
                <input data-testid="cf-company" placeholder="Company / Organisation" className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-shramik-green" value={form.company} onChange={e => setForm({...form, company:e.target.value})}/>
                <input required data-testid="cf-mobile" placeholder="Mobile *" className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-shramik-green" value={form.mobile} onChange={e => setForm({...form, mobile:e.target.value})}/>
                <input required type="email" data-testid="cf-email" placeholder="Email *" className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-shramik-green" value={form.email} onChange={e => setForm({...form, email:e.target.value})}/>
                <input required data-testid="cf-subject" placeholder="Subject *" className="md:col-span-2 w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-shramik-green" value={form.subject} onChange={e => setForm({...form, subject:e.target.value})}/>
                <textarea required data-testid="cf-message" placeholder="How can we help? *" rows={5} className="md:col-span-2 w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-shramik-green resize-none" value={form.message} onChange={e => setForm({...form, message:e.target.value})}/>
              </div>

              <div className="mt-6 flex flex-col-reverse md:flex-row justify-between md:items-center gap-4">
                <p className="text-xs text-slate-500">By submitting, you agree to our privacy policy.</p>
                <button data-testid="cf-submit" disabled={loading} className="btn-primary disabled:opacity-70">
                  {loading ? "Sending..." : "Send message"} <Send className="w-4 h-4"/>
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16" data-testid="map-section">
        <div className="container-page">
          <div className="overline mb-3">Find us</div>
          <h2 className="font-display font-light text-3xl md:text-5xl tracking-tight mb-8 max-w-3xl text-balance">On the map — and in every branch we serve.</h2>
          <div className="rounded-3xl overflow-hidden border border-slate-100 shadow-xl aspect-[16/9]">
            <iframe
              title="Shramik Group Location"
              src="https://www.google.com/maps?q=Dhandarphal%20Bk%2C%20Sangamner%2C%20Ahilyanagar%2C%20Maharashtra&output=embed"
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* Branches */}
      <section className="py-16 bg-slate-50" data-testid="branches-section">
        <div className="container-page">
          <div className="overline mb-3">Our Branches</div>
          <h2 className="font-display font-light text-3xl md:text-5xl tracking-tight mb-10 max-w-3xl text-balance">15+ locations across <em className="not-italic font-serif italic text-shramik-green">Maharashtra.</em></h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {BRANCHES.map((b, i) => (
              <div key={b} data-testid={`branch-${i}`} className="p-5 rounded-2xl bg-white border border-slate-100 hover:border-shramik-green hover:shadow-md transition-all">
                <MapPin className="w-4 h-4 text-shramik-green mb-2"/>
                <div className="font-display font-medium">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
